import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { IconCalendarMark, SkeletonText, useNavigationPort } from '@taki/ui';
import { useAppRuntime } from '@taki/app-runtime';
import type { ProprietesDeVueCanvas } from '@taki/app-runtime';
import type { Asset, CareRecord, Product } from '@taki/data-model';
import { Alertes, Onglets, Section, instantDe, raisonDe, sac } from './dossier/morceaux.js';
import type { Fiche, Onglet } from './dossier/morceaux.js';
import { datePureEnTexte, heureDe, valeurEnTexte } from './dossier/temps.js';
import { nombreEnTexte } from './dossier/nombre.js';
import { Carnet } from './dossier/OngletCarnet.js';
import { produitsPour } from './dossier/carnet.js';
import { CadreAFaire, CadreDApercu, CadreSonHistorique, EnTeteDuPatient, LigneDApercu, OngletHistorique, OngletTaches } from './dossier/briques.js';
import { useAnnuaire } from './dossier/useAnnuaire.js';
import { useDossierDeLAnimal } from './dossier/useDossierDeLAnimal.js';
import { NouvelleVisite } from './NouvelleVisite.js';
import type { VisiteDemandee } from './NouvelleVisite.js';
import { consultationDeLaVisite } from './visite.js';
/** Ce que la vue recoit du socle pour poser ses gestes dans la barre de la fiche. */
type PoserActions = ProprietesDeVueCanvas['poserActions'] | undefined;

/**
 * « DOSSIER DE L'ANIMAL » : une VUE DE FICHE Canvas des animaux (regle G23,
 * accord dans `canvas.views` de `taki.app.json`, carte dans `vuesCustom` de
 * `taki.screens.json`, `docs/socle/fiche-suivi.md` section 3). Le tableau de
 * bord d'un animal, offert dans le selecteur de vues de sa fiche, a cote de la
 * Fiche native.
 *
 * ON LIT ICI, ON MODIFIE DANS LA FICHE. Les champs de l'animal se modifient
 * dans la vue « Fiche » du selecteur ; une visite se vit dans l'app
 * Consultations (« Consulter maintenant », geste declare de la porte, ou un clic
 * sur une consultation, par le port de navigation du shell). Gestes legers
 * seulement : cocher ou creer une tache, planifier une visite.
 *
 * TOUT PASSE PAR LE SDK (`useAppRuntime`), jamais par Firestore : les rules
 * jugent chaque lecture et chaque ecriture.
 */

/** Une consultation, ou une tache, ouverte dans l'app qui la montre. */
type OuvrirAilleurs = ((id: string) => void) | null;

/** LA VUE : rend le dossier de la fiche ouverte, remonte quand on change d'animal. */
export function DossierDeLAnimal(props: ProprietesDeVueCanvas): JSX.Element | null {
  const { fiche } = props;
  if (fiche === null) return null;
  const { poserActions } = props;
  return <DossierOuvert key={String(fiche['id'])} animal={fiche as unknown as Asset} poserActions={poserActions} />;
}

function DossierOuvert({ animal, poserActions }: { animal: Asset; poserActions: PoserActions }): JSX.Element {
  const { sdk, orgId = '' } = useAppRuntime();
  const navigation = useNavigationPort();
  const { moi, personnes, nomDePersonne } = useAnnuaire();

  /* LE CATALOGUE, une fois : le carnet de vaccination lit la prevention des produits. */
  const [catalogue, setCatalogue] = useState<readonly Product[]>([]);
  const [catalogueLu, setCatalogueLu] = useState(false);
  const [erreurDuCatalogue, setErreurDuCatalogue] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    void sdk.products.list({ type: 'article', limit: 500 }).then(
      (produits) => {
        if (!vivant) return;
        setCatalogue(produits);
        setCatalogueLu(true);
      },
      (cause: unknown) => {
        if (!vivant) return;
        setErreurDuCatalogue(`Catalogue illisible : ${raisonDe(cause)}`);
        setCatalogueLu(true);
      },
    );
    return () => {
      vivant = false;
    };
  }, [sdk]);

  /* UNE CONSULTATION, ET UNE TACHE, S'OUVRENT DANS L'APP QUI LES MONTRE : le
     shell connait la porte (Consultations), cette app ne connait pas les autres.
     Sans destination (app non installee), le geste n'est pas propose. */
  const destinationDeLaVisite = navigation?.destination('careRecords', 'consultation') ?? null;
  const destinationDeLaTache = navigation?.destination('todos', 'dossier') ?? null;
  const ouvrirConsultation: OuvrirAilleurs =
    navigation === null || destinationDeLaVisite === null ? null : (id) => navigation.ouvrir(destinationDeLaVisite, id, animal.name);
  const ouvrirTache: OuvrirAilleurs =
    navigation === null || destinationDeLaTache === null ? null : (id) => navigation.ouvrir(destinationDeLaTache, id, animal.name);

  const [onglet, setOnglet] = useState('apercu');
  /* Le jour de reference est fige a l'ouverture : les calculs ne bougent pas a chaque rendu. */
  const [maintenant] = useState(() => new Date());

  /* LES CONSULTATIONS DE L'ANIMAL, passees (l'historique) et a venir. */
  const [consultations, setConsultations] = useState<readonly Fiche[]>([]);
  const [visitesLues, setVisitesLues] = useState(false);
  const [erreurDesVisites, setErreurDesVisites] = useState<string | null>(null);
  const rechargerLesVisites = useCallback(async (): Promise<void> => {
    try {
      const liste = await sdk.careRecords.list({ type: 'consultation', where: { assetId: animal.id }, limit: 200 });
      setConsultations(liste as unknown as Fiche[]);
      setErreurDesVisites(null);
    } catch (cause) {
      setErreurDesVisites(`Consultations illisibles : ${raisonDe(cause)}`);
    } finally {
      setVisitesLues(true);
    }
  }, [sdk, animal.id]);
  useEffect(() => {
    void rechargerLesVisites();
  }, [rechargerLesVisites]);
  const historique = useMemo(
    () =>
      consultations
        .filter((c) => (instantDe(c)?.getTime() ?? 0) <= maintenant.getTime())
        .sort((a, b) => (instantDe(b)?.getTime() ?? 0) - (instantDe(a)?.getTime() ?? 0)),
    [consultations, maintenant],
  );
  const aVenir = useMemo(
    () =>
      consultations
        .filter((c) => (instantDe(c)?.getTime() ?? 0) > maintenant.getTime())
        .sort((a, b) => (instantDe(a)?.getTime() ?? 0) - (instantDe(b)?.getTime() ?? 0)),
    [consultations, maintenant],
  );

  const dossier = useDossierDeLAnimal({ sdk, orgId, animal, historique, catalogue, nomDePersonne, quand: maintenant });
  const { fiche, soins, taches, tachesOuvertes, carnet, produitsDuCarnet } = dossier;
  const erreur = dossier.erreur ?? erreurDesVisites ?? erreurDuCatalogue;
  /* Tant que les visites, le catalogue, les soins et l'historique clinique ne sont pas lus : le squelette du socle, jamais un « Aucun » qui ment. */
  const chargement = !catalogueLu || !visitesLues || !dossier.lu;

  /* UNE VISITE PLANIFIEE : elle reste ici, « Prevue ». « Consulter maintenant » n'est plus posee par cette vue : c'est le geste declare de la porte (fenetre `consulterMaintenant`), rendu par le socle sur toutes les vues de la fiche. */
  const [planification, setPlanification] = useState(false);
  const [annonce, setAnnonce] = useState<string | null>(null);
  const planifier = async (demande: VisiteDemandee): Promise<boolean> => {
    try {
      await sdk.careRecords.create(consultationDeLaVisite(orgId, animal.id, demande));
      setPlanification(false);
      dossier.setErreur(null);
      if (demande.quand !== null) setAnnonce(`Visite prévue le ${demande.quand.toLocaleDateString('fr-BE')} à ${heureDe(demande.quand)}.`);
      await rechargerLesVisites();
      return true;
    } catch (cause) {
      dossier.setErreur(`Visite non créée : ${raisonDe(cause)}`);
      return false;
    }
  };

  const produit = (productId: unknown): Product | undefined => catalogue.find((p) => p.id === productId);
  const soinsParDate = [...soins].sort((a, b) => String(sac(b)['careDate'] ?? '').localeCompare(String(sac(a)['careDate'] ?? '')));

  const ONGLETS: readonly Onglet[] = [
    { id: 'apercu', libelle: 'Aperçu' },
    { id: 'historique', libelle: 'Historique', compte: chargement ? undefined : historique.length },
    { id: 'rappels', libelle: 'Vaccins et rappels', point: !chargement && carnet.some((l) => l.etat === 'retard' || (l.etat === 'jamais' && l.obligatoire)) },
    { id: 'soins', libelle: 'Soins et produits', compte: chargement ? undefined : soins.length },
    { id: 'taches', libelle: 'Tâches', compte: chargement ? undefined : tachesOuvertes.length },
  ];

  /*
   * LA BARRE D'ACTION DE LA FICHE : « Planifier » dans le menu. « Consulter
   * maintenant », lui, n'est pas pose ici : la porte `animaux` le declare
   * (`niveau: "fiche"`), le socle le rend a gauche sur la Fiche standard comme
   * sur ce dossier, et sa fenetre (`FenetreConsulterMaintenant`) dit elle-meme
   * que l'app Consultations manque (G16). Le corps n'a aucun bouton ; le
   * formulaire de la visite s'ouvre dans une fenetre.
   */
  const poser = useRef(poserActions);
  poser.current = poserActions;
  useEffect(() => {
    poser.current?.({
      menu: [{ id: 'planifier', libelle: 'Planifier', onClick: () => setPlanification(true) }],
    });
  }, []);
  useEffect(() => () => poser.current?.(null), []);

  /* pb-36 : les 128 px du coin de la mascotte de Lia restent vides. */
  return (
    <div className="flex w-full flex-col gap-6 px-5 pb-36 pt-6" data-vue="patients.dossier">
      {erreur !== null ? <p className="text-sm text-danger">{erreur}</p> : null}

      <EnTeteDuPatient
        animal={animal}
        proprietaire={dossier.proprietaire}
        quand={maintenant}
        derniere={dossier.derniere}
        prochain={dossier.prochain}
        chargement={chargement}
      />
      {planification ? (
        <NouvelleVisite
          mode="planifier"
          personnes={personnes}
          veterinaireParDefaut={moi}
          onCreer={planifier}
          onAnnuler={() => setPlanification(false)}
        />
      ) : null}
      {annonce === null ? null : <p className="-mt-3 text-sm text-ink-700">{annonce}</p>}
      <Alertes valeurs={fiche['alerts']} notes={fiche['internalNotes']} />

      <Onglets onglets={ONGLETS} actif={onglet} onChoisir={setOnglet} />

      {onglet === 'apercu' ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CadreAFaire chargement={chargement} titre="À faire ou proposer" aProposer={dossier.aProposer} tachesOuvertes={tachesOuvertes} nomDePersonne={nomDePersonne} />
          <CadreDApercu icone={<IconCalendarMark size={18} />} titre="Prochaines visites">
            {chargement ? <SkeletonText lines={3} /> : null}
            {!chargement && aVenir.length === 0 ? <p className="text-sm text-ink-500">Aucune visite prévue.</p> : null}
            <dl className="flex flex-col text-sm">
              {aVenir.map((c) => {
                const quand = instantDe(c);
                return (
                  <LigneDApercu key={String(c['id'])} titre={quand === null ? '' : `${quand.toLocaleDateString('fr-BE')} ${heureDe(quand)}`}>
                    <LienVersLaVisite id={String(c['id'])} ouvrir={ouvrirConsultation}>
                      {[valeurEnTexte(sac(c)['reason']), nomDePersonne(sac(c)['veterinarian'])].filter((x) => x !== '').join(' · ') || 'Consultation'}
                    </LienVersLaVisite>
                  </LigneDApercu>
                );
              })}
            </dl>
          </CadreDApercu>
          <div className="lg:col-span-2">
            <CadreSonHistorique chargement={chargement} lignes={dossier.lignesDuPasse} poids={dossier.poids} vide="Aucune consultation." onOuvrirConsultation={ouvrirConsultation} />
          </div>
        </div>
      ) : null}

      {onglet === 'historique' && chargement ? (
        <Section titre="Historique">
          <SkeletonText lines={5} />
        </Section>
      ) : null}
      {onglet === 'historique' && !chargement ? (
        <OngletHistorique sdk={sdk} consultations={historique} vide="Aucune consultation." onOuvrirConsultation={ouvrirConsultation} />
      ) : null}

      {onglet === 'rappels' && chargement ? (
        <Section titre="Vaccins et rappels">
          <SkeletonText lines={5} />
        </Section>
      ) : null}
      {onglet === 'rappels' && !chargement ? (
        /* Pas d'ajout ici : un vaccin se fait en consultation, ou il devient une ligne a facturer. */
        <Carnet
          espece={valeurEnTexte(fiche['species'])}
          lignes={carnet}
          produitsPour={(protocole) => produitsPour(protocole, fiche['species'], produitsDuCarnet)}
          peutAjouter={false}
          onAjouter={async () => undefined}
        />
      ) : null}

      {onglet === 'soins' ? (
        <Section titre="Soins et produits">
          {chargement ? (
            <SkeletonText lines={5} />
          ) : soinsParDate.length === 0 ? (
            <p className="text-sm text-ink-500">Aucun soin pour cet animal.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-soft text-left text-xs text-ink-500">
                  <th className="w-28 py-2 font-semibold">Date</th>
                  <th className="py-2 font-semibold">Produit</th>
                  <th className="w-36 py-2 font-semibold">Lot</th>
                  <th className="py-2 text-right font-semibold">Quantité</th>
                  <th className="w-28 py-2 pl-2 font-semibold">Unité</th>
                </tr>
              </thead>
              <tbody>
                {soinsParDate.map((s: CareRecord) => (
                  <tr key={s.id} className="border-b border-border-soft">
                    <td className="py-2 tabular-nums text-ink-700">{datePureEnTexte(sac(s)['careDate'])}</td>
                    <td className="py-2 text-ink-900">{produit(sac(s)['productId'])?.name ?? 'Produit'}</td>
                    <td className="py-2 text-ink-700">{valeurEnTexte(sac(s)['lotNumber'])}</td>
                    <td className="py-2 text-right tabular-nums text-ink-700">{typeof sac(s)['quantity'] === 'number' ? nombreEnTexte(sac(s)['quantity'] as number) : '1'}</td>
                    <td className="py-2 pl-2 text-ink-700">{produit(sac(s)['productId'])?.unit ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      ) : null}

      {onglet === 'taches' && chargement ? (
        <Section titre="Tâches du dossier">
          <SkeletonText lines={4} />
        </Section>
      ) : null}
      {onglet === 'taches' && !chargement ? (
        <OngletTaches
          taches={taches}
          animal={animal.name}
          personnes={personnes}
          assigneParDefaut={moi}
          nomDePersonne={nomDePersonne}
          onBasculer={(t) => void dossier.basculerTache(t)}
          onOuvrir={ouvrirTache === null ? null : (t) => ouvrirTache(t.id)}
          onCreer={(t) => dossier.creerTache(t, null)}
        />
      ) : null}
    </div>
  );
}

/** Une ligne qui ouvre la visite quand une app la montre, sinon un simple texte. */
function LienVersLaVisite({ id, ouvrir, children }: { id: string; ouvrir: OuvrirAilleurs; children: ReactNode }): JSX.Element {
  if (ouvrir === null) return <span>{children}</span>;
  return (
    <button type="button" className="text-left hover:underline focus-visible:outline-none" onClick={() => ouvrir(id)}>
      {children}
    </button>
  );
}

export default DossierDeLAnimal;
